// アプリケーション状態管理
import type { ImportedApp, SettingsData, NICConfig, NICConfigStorage, VariablesStorage } from '../types/index.js';

// 永続化用キー
export const STORAGE_KEY_SELECTED_IDS = 'winbatchgen_selected_ids_v1';
export const STORAGE_KEY_CATEGORY_STATE = 'winbatchgen_category_state_v1';
export const STORAGE_KEY_LEFT_SCROLL = 'winbatchgen_left_scroll_v1';
export const STORAGE_KEY_VARIABLES = 'winbatchgen_variables_v1';
export const STORAGE_KEY_NIC_CONFIG = 'winbatchgen_nic_config_v1';
export const STORAGE_KEY_PREVIEW_HEIGHT = 'winbatchgen_preview_height_v1';

// アプリケーションの状態
let importedApps: ImportedApp[] = [];
let settings: SettingsData | null = null;
let lastGeneratedCode: string = '';

// 状態の取得
export function getImportedApps(): ImportedApp[] {
    return importedApps;
}

export function setImportedApps(apps: ImportedApp[]): void {
    importedApps = apps;
}

export function getSettings(): SettingsData | null {
    return settings;
}

export function setSettings(data: SettingsData | null): void {
    settings = data;
}

export function getLastGeneratedCode(): string {
    return lastGeneratedCode;
}

export function setLastGeneratedCode(code: string): void {
    lastGeneratedCode = code;
}

// 選択状態の保存・復元
export function saveSelectionState(selectedIds: string[]): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY_SELECTED_IDS, JSON.stringify(selectedIds));
    } catch (e) {
        console.warn('選択状態の保存に失敗しました:', e);
    }
}

export function restoreSelectionState(): string[] {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    
    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_IDS);
    } catch {
        return [];
    }
    if (!raw) return [];

    let ids: string[] = [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            ids = parsed;
        }
    } catch {
        return [];
    }
    return ids;
}

// カテゴリ状態の保存・復元
export function saveCategoryState(state: Record<string, boolean>): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY_CATEGORY_STATE, JSON.stringify(state));
    } catch (e) {
        console.warn('カテゴリ状態の保存に失敗しました:', e);
    }
}

export function restoreCategoryState(): Record<string, boolean> {
    if (typeof window === 'undefined' || !window.localStorage) return {};

    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(STORAGE_KEY_CATEGORY_STATE);
    } catch {
        return {};
    }
    if (!raw) return {};

    let state: Record<string, boolean> = {};
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            state = parsed as Record<string, boolean>;
        }
    } catch {
        return {};
    }
    return state;
}

// スクロール位置の保存・復元
export function saveLeftScroll(scrollTop: number): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY_LEFT_SCROLL, String(scrollTop));
    } catch (e) {
        console.warn('スクロール位置の保存に失敗しました:', e);
    }
}

export function restoreLeftScroll(): number {
    if (typeof window === 'undefined' || !window.localStorage) return 0;
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_LEFT_SCROLL);
        if (raw) {
            return parseInt(raw, 10) || 0;
        }
    } catch {
        return 0;
    }
    return 0;
}

// 変数の保存・復元
export function saveVariableValue(itemId: string, varName: string, value: string): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    try {
        let variables: VariablesStorage = {};
        const raw = window.localStorage.getItem(STORAGE_KEY_VARIABLES);
        if (raw) {
            variables = JSON.parse(raw) as VariablesStorage;
        }
        
        if (!variables[itemId]) {
            variables[itemId] = {};
        }
        variables[itemId][varName] = value;
        
        window.localStorage.setItem(STORAGE_KEY_VARIABLES, JSON.stringify(variables));
    } catch (error) {
        console.error('変数の保存エラー:', error);
    }
}

export function getVariableValue(itemId: string, varName: string, defaultValue: string): string {
    if (typeof window === 'undefined' || !window.localStorage) return defaultValue;
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_VARIABLES);
        if (!raw) return defaultValue;
        
        const variables: VariablesStorage = JSON.parse(raw);
        const itemVars = variables[itemId];
        const varValue = itemVars?.[varName];
        if (varValue !== undefined && typeof varValue === 'string') {
            return varValue;
        }
    } catch {
        return defaultValue;
    }
    return defaultValue;
}

export function restoreVariableValues(): VariablesStorage {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_VARIABLES);
        if (!raw) return {};
        return JSON.parse(raw) as VariablesStorage;
    } catch {
        return {};
    }
}

// NIC設定の保存・復元
export function saveNICConfig(itemId: string, nics: NICConfig[]): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    try {
        let config: NICConfigStorage = {};
        const raw = window.localStorage.getItem(STORAGE_KEY_NIC_CONFIG);
        if (raw) {
            config = JSON.parse(raw) as NICConfigStorage;
        }
        config[itemId] = nics;
        window.localStorage.setItem(STORAGE_KEY_NIC_CONFIG, JSON.stringify(config));
    } catch (error) {
        console.error('NIC設定の保存エラー:', error);
    }
}

export function getNICConfig(itemId: string): NICConfig[] {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_NIC_CONFIG);
        if (!raw) return [];
        
        const config: NICConfigStorage = JSON.parse(raw);
        return config[itemId] || [];
    } catch {
        return [];
    }
}

// プレビュー高さの保存・復元
export function savePreviewHeight(height: number): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY_PREVIEW_HEIGHT, String(height));
    } catch (e) {
        console.warn('プレビュー高さの保存に失敗しました:', e);
    }
}

export function restorePreviewHeight(): number | null {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_PREVIEW_HEIGHT);
        if (raw) {
            return parseInt(raw, 10) || null;
        }
    } catch {
        return null;
    }
    return null;
}

